/**
 * Workspace state for the whole app. Loads once from this browser's storage,
 * then saves every change shortly after it happens. If storage is unavailable
 * the app still works, and says progress will not be kept.
 */

import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { emptyWorkspace, nowIso, type Workspace } from '../workspace/schema.js';
import { isPersistent, loadWorkspace, saveWorkspace } from '../workspace/store.js';

interface WorkspaceCtx {
  ws: Workspace;
  loaded: boolean;
  persistent: boolean;
  loadError: string | null;
  update: (fn: (ws: Workspace) => Workspace) => void;
  replace: (ws: Workspace) => void;
}

const Ctx = createContext<WorkspaceCtx | null>(null);

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const [ws, setWs] = useState<Workspace>(() => emptyWorkspace());
  const [loaded, setLoaded] = useState(false);
  const [persistent, setPersistent] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const saveTimer = useRef<number | undefined>(undefined);

  useEffect(() => {
    let alive = true;
    (async () => {
      const p = await isPersistent();
      try {
        const saved = await loadWorkspace();
        if (alive && saved) setWs(saved);
      } catch (e) {
        if (alive) setLoadError(e instanceof Error ? e.message : 'Saved progress could not be read.');
      }
      if (alive) { setPersistent(p); setLoaded(true); }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!loaded) return;
    window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => { void saveWorkspace(ws); }, 250);
    return () => window.clearTimeout(saveTimer.current);
  }, [ws, loaded]);

  const update = useCallback((fn: (w: Workspace) => Workspace) => {
    setWs((cur) => ({ ...fn(cur), updatedAt: nowIso() }));
  }, []);

  const replace = useCallback((next: Workspace) => setWs(next), []);

  return <Ctx.Provider value={{ ws, loaded, persistent, loadError, update, replace }}>{children}</Ctx.Provider>;
}

export function useWorkspace(): WorkspaceCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('useWorkspace outside WorkspaceProvider');
  return c;
}
